/**
 * Central date formatting + parsing aware of the user's preference.
 *
 * Import ``useDateFormat`` in React components:
 *   const { format, parse } = useDateFormat()
 *   format(someDate) // "23/04/2026" for a DD/MM/YYYY user
 *
 * For non-component code (services, reducers) call ``formatDate`` with an
 * explicit token. Keep tokens identical to the backend ``User.date_format``
 * choices so round-trips are lossless.
 */
import { useMemo } from "react"
import { useAuth } from "@/hooks/useAuth"

export type DateFormatToken =
  | "DD/MM/YYYY"
  | "MM/DD/YYYY"
  | "YYYY-MM-DD"
  | "DD.MM.YYYY"
  | "DD-MM-YYYY"

export const DEFAULT_DATE_FORMAT: DateFormatToken = "DD/MM/YYYY"

const SEPARATOR_BY_TOKEN: Record<DateFormatToken, string> = {
  "DD/MM/YYYY": "/",
  "MM/DD/YYYY": "/",
  "YYYY-MM-DD": "-",
  "DD.MM.YYYY": ".",
  "DD-MM-YYYY": "-",
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`
}

/** Format a date-ish value using the given token. */
export function formatDate(
  value: Date | string | number | null | undefined,
  token: DateFormatToken = DEFAULT_DATE_FORMAT,
): string {
  if (value == null || value === "") return ""
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ""

  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())

  switch (token) {
    case "MM/DD/YYYY":
      return `${mm}/${dd}/${yyyy}`
    case "YYYY-MM-DD":
      return `${yyyy}-${mm}-${dd}`
    case "DD.MM.YYYY":
      return `${dd}.${mm}.${yyyy}`
    case "DD-MM-YYYY":
      return `${dd}-${mm}-${yyyy}`
    case "DD/MM/YYYY":
    default:
      return `${dd}/${mm}/${yyyy}`
  }
}

/**
 * Permissive parser that accepts:
 *  - the user's preferred format
 *  - ISO 8601 (always)
 *  - dates with ``-``, ``/`` or ``.`` separators
 * so pasted values work even when they don't match the preference.
 * Returns ``null`` on failure rather than ``Invalid Date``.
 */
export function parseDate(
  input: string,
  token: DateFormatToken = DEFAULT_DATE_FORMAT,
): Date | null {
  if (!input) return null
  const trimmed = input.trim()
  if (!trimmed) return null

  // ISO fast path.
  const iso = Date.parse(trimmed)
  if (!Number.isNaN(iso) && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return new Date(iso)
  }

  const parts = trimmed.split(/[\/\-\.]/).map((p) => p.trim())
  if (parts.length !== 3) return null

  let day: number, month: number, year: number
  if (token === "MM/DD/YYYY") {
    month = parseInt(parts[0], 10)
    day = parseInt(parts[1], 10)
    year = parseInt(parts[2], 10)
  } else if (token === "YYYY-MM-DD") {
    year = parseInt(parts[0], 10)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else {
    // DD/MM/YYYY family (EU default + DD.MM / DD-MM variants).
    day = parseInt(parts[0], 10)
    month = parseInt(parts[1], 10)
    year = parseInt(parts[2], 10)
  }

  if (
    !Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year) ||
    day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 3000
  ) {
    return null
  }

  const out = new Date(year, month - 1, day)
  return Number.isNaN(out.getTime()) ? null : out
}

/** ISO representation suitable for sending back to the API. */
export function toISODate(value: Date | string | null | undefined): string {
  if (!value) return ""
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Hook: resolves the token from the logged-in user. */
export function useDateFormat() {
  const { user } = useAuth()
  const token = (user as any)?.date_format as DateFormatToken | undefined
  const active: DateFormatToken = token || DEFAULT_DATE_FORMAT
  const separator = SEPARATOR_BY_TOKEN[active]

  return useMemo(
    () => ({
      token: active,
      separator,
      format: (v: Date | string | number | null | undefined) => formatDate(v, active),
      parse: (v: string) => parseDate(v, active),
      placeholder: active.toLowerCase(),
    }),
    [active, separator],
  )
}
